import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import some from 'lodash/some'
import React, { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { FormContext, FormContextProps } from '@Shared/Components/Forms/FormContext'
import { FormCollectionProps, FormState, FormValidation, FormValidationAction } from '@Shared/Components/Forms/FormElement.types'

export const FormGroupDeprecated = forwardRef((
  {
    formCollectionActionsRef,
    children,
    className,
    id,
    model,
    onBlur,
    onChange,
    onSubmit,
    updateModel,
    validateOn = FormValidationAction.BLUR,
  }: React.PropsWithChildren<FormCollectionProps<{}>>,
  ref: ForwardedRef<HTMLFormElement | null>,
) => {
  const [validationResults, setValidationResults] = useState<Record<string, FormValidation>>({})
  const [validations, setValidations] = useState<Record<string, Function>>({})
  const [initialBlur, setInitialBlur] = useState(false)
  const [formValid, setFormValid] = useState<boolean>(false)
  const [submitAttempts, setSubmitAttempts] = useState<number>(0)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [elementBlurred, setElementBlurred] = useState(0)
  const [activeID, setActiveID] = useState<string | null>(null)

  const updateValidationResults = useCallback((key: string, value: FormValidation) => {
    setValidationResults(prevState => ({ ...prevState, [key]: value }))
  }, [])

  const getFormValid = () => {
    const validationValues = Object.values(validationResults)
    return validationValues.length > 0 && !some(validationValues, res => res.validity === FormState.ERROR)
  }

  useEffect(() => {
    const formIsValid = getFormValid()
    setFormValid(formIsValid)

    if (formIsValid && submitAttempted && onSubmit) {
      onSubmit(model, formIsValid, validationResults, null)
      setSubmitAttempted(false)
    } else if (!formIsValid && submitAttempted) {
      const validationError = find(Object.keys(validationResults), key => validationResults[key].validity === FormState.ERROR)

      const formElement = document.getElementById(id)?.querySelector(`[name="${validationError}"]`)
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      setSubmitAttempted(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationResults, submitAttempted])

  const triggerBlur = () => {
    const formIsValid = getFormValid()
    setFormValid(formIsValid)
    if (!isEmpty(validationResults) && validateOn === FormValidationAction.BLUR && onBlur) {
      onBlur(model, formIsValid, validationResults, activeID)
    }
  }

  useEffect(() => {
    triggerBlur()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementBlurred])

  useEffect(() => {
    if (!isEmpty(validationResults) && !initialBlur) {
      setInitialBlur(true)
      triggerBlur()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationResults])

  useEffect(() => {
    if (onChange && validateOn !== FormValidationAction.BLUR) {
      onChange(model, formValid, validationResults, activeID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, formValid, onChange])

  const formValue: FormContextProps<{}> = useMemo(() => ({
    model,
    submitAttempts,
    updateModel,
    updateValidationResults,
    validateOn,
    validationResults,
    updateValidationFunctions: (key, fn) => setValidations(prev => ({ ...prev, [key]: fn })),
    setActiveID,
    blurElement: () => setElementBlurred(prev => prev + 1),
  }), [model, submitAttempts, updateModel, updateValidationResults, validateOn, validationResults, setValidations])

  const validate = () => {
    setActiveID(null)
    setSubmitAttempts(prev => prev + 1)
    setSubmitAttempted(true)
    if (onSubmit) {
      onSubmit(model, formValid, validationResults, null)
    }
  }

  useImperativeHandle(formCollectionActionsRef, () => ({
    validate: () => {
      const res = Object.values(validations).every(validateFn => validateFn())
      validate()
      return res
    },
  }))

  return (
    <FormContext.Provider value={formValue}>
      <form
        className={className}
        id={id}
        onSubmit={(event) => {
          event.preventDefault()
          validate()
        }}
        ref={ref}
      >
        {children}
      </form>
    </FormContext.Provider>
  )
})
